import { Box, IconButton } from '@mui/material';
import {
  GridGroupNode,
  GridRenderCellParams,
  GridTreeNode,
  useGridApiContext,
} from '@mui/x-data-grid-pro';
import { ExpandMore, ChevronRight } from '@mui/icons-material';
import { Stack, styled } from '@mui/system';
import { AdminSettingsRowItem } from './AdminSettings';

const PREFIX = 'TransportationAttributesGroupCell';

const classes = {
  arrowButton: `${PREFIX}-arrowButton`,
  contentContainer: `${PREFIX}-contentContainer`,
  descriptionText: `${PREFIX}-descriptionText`,
};

interface StyledCellProps {
  depth: number;
}

const GroupCellBox = styled(Box)<StyledCellProps>(({ theme, depth }) => ({
  display: 'flex',
  alignItems: 'center',
  width: '100%',

  [`& .${classes.arrowButton}`]: {
    padding: '4px',
  },
}));

const SingleCellBox = styled(Box)<StyledCellProps>(({ theme, depth }) => ({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  paddingLeft: 35,
  overflow: 'hidden',

  [`& .${classes.contentContainer}`]: {
    overflow: 'hidden',
    width: '100%',
  },
  [`& .${classes.descriptionText}`]: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    color: theme.palette.lightTextSecondary.main,
    width: 'calc(100% - 50px)',
  },
}));

export const AdminSettingsGroupCell = (
  params: GridRenderCellParams<AdminSettingsRowItem>,
) => {
  const apiRef = useGridApiContext();
  const rowNode: GridTreeNode | null = apiRef.current.getRowNode(params.id);

  // Check if this is a group/parent row
  const isGroupRow = params.row.hasChild && rowNode?.type === 'group';
  const depth: number = rowNode?.depth ?? 0;

  const { category, description } = params.row;
  if (!isGroupRow) {
    return (
      <SingleCellBox depth={depth}>
        <Stack direction="column" className={classes.contentContainer}>
          <Box>{category}</Box>
          {description && (
            <Box className={classes.descriptionText}>{description}</Box>
          )}
        </Stack>
      </SingleCellBox>
    );
  }

  // For group rows, we need to render the default cell structure manually
  // to preserve the expand/collapse functionality
  const isExpanded: boolean =
    (rowNode as GridGroupNode)?.childrenExpanded ?? false;

  // Create the expand/collapse button click handler
  const handleIconClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (isExpanded) {
      apiRef.current.setRowChildrenExpansion(params.id, false);
    } else {
      apiRef.current.setRowChildrenExpansion(params.id, true);
    }
  };

  return (
    <GroupCellBox depth={depth}>
      <IconButton
        size="small"
        onClick={handleIconClick}
        className={classes.arrowButton}>
        {isExpanded ? <ExpandMore /> : <ChevronRight />}
      </IconButton>

      <Box>{category}</Box>
    </GroupCellBox>
  );
};
